import { generateApolloClient } from "@/common/apolloClientUtil";
import { CsvFileUploadSetting as setting } from "./constant";
import { ApolloClient, DocumentNode } from "@apollo/client/core";

interface UploadInput {
  customer_code: string;
  chain_code: string;
  shop_code?: string;
  csv: string;
  current_chain_code?: string;
  user_id: string;
}

interface MutationResult {
  result: string;
  message?: string | string[] | null;
  detail?:
    | { [key: string]: { row_number: number | string; message: string[] | string } }
    | Array<{ row_number: number | string; message: string[] | string }>
    | null;
}

interface MutationVariables {
  input: UploadInput;
}

export interface FileReaderParams {
  file: File;
  createUploadInput: (base64: string, chainCode: string) => UploadInput;
  mutationDocument: DocumentNode;
  resultDataKey: string;
  chainCode?: string;
  csv_split_unit?: number;
  handleUploadComplete: (failed: boolean, message: string) => void;
  handleError: (error: unknown, message: string) => void;
}

export class CsvFileUpload {
  static async execute(params: FileReaderParams): Promise<void> {
    const client: ApolloClient<unknown> = await generateApolloClient();
    const reader: FileReader = new FileReader();
    const csv_split_unit: number =
      params.csv_split_unit ?? setting.default_csv_split_unit;

    reader.onload = async (e: ProgressEvent<FileReader>) => {
      const target: FileReader = e.target as FileReader;
      if (!target.result) {
        return;
      }
      const csvData: string = target.result as string;
      const csvSplitData: string[] = this.splitCsvData(csvData, csv_split_unit);
      if (csvSplitData.length === 0) {
        // ヘッダのみ/空ファイルはサーバーに送らず、即座に失敗として扱う。
        params.handleUploadComplete(
          true,
          "アップロードに失敗しました。\n登録するデータがCSVファイルに存在しません。\n"
        );
        return;
      }
      let failed = false;
      const errorDetails: string[] = [];
      // 分割送信時に同一エラーが繰り返し返るため、表示は一意化する。
      const seenErrors = new Set<string>();
      let completeMessage = "";
      // 例外発生時に handleUploadComplete の二重呼び出しを防ぐフラグ
      let caught = false;

      try {
        for (const [index, csvLine] of csvSplitData.entries()) {
          const csvBase64Data = this.toBase64String(csvLine);
          const input: UploadInput = params.createUploadInput(
            csvBase64Data,
            params.chainCode!
          );

          const result = await client.query<MutationResult, MutationVariables>({
            query: params.mutationDocument,
            variables: { input },
            fetchPolicy: "no-cache",
          });

          const uploadResult = (
            result.data as unknown as Record<string, MutationResult>
          )[params.resultDataKey];
          if (!uploadResult || !uploadResult.result) {
            // 想定外レスポンスは「成功」扱いにしない。
            failed = true;
            const message = "アップロード結果の取得に失敗しました。\n";
            if (!seenErrors.has(message)) {
              errorDetails.push(message);
              seenErrors.add(message);
            }
            continue;
          }
          if (uploadResult && uploadResult.result !== "Completed") {
            failed = true;
            const base_row_number: number = csv_split_unit * index;
            const details = uploadResult.detail;
            if (details) {
              const detailList = Array.isArray(details)
                ? details
                : Object.values(details);
              for (const detail of detailList) {
                const row_number: number = base_row_number + Number(detail.row_number);
                const result_message: string = Array.isArray(detail.message)
                  ? detail.message.join("")
                  : detail.message;
                const message = `${row_number}行目: ${result_message}\n`;
                if (!seenErrors.has(message)) {
                  errorDetails.push(message);
                  seenErrors.add(message);
                }
              }
            } else if (uploadResult.message) {
              // 列数/列名不正など detail が無いエラーはCSV全体の不正を示すため、
              // 同じ結果を増やさないようここで中断する。
              const result_message: string = Array.isArray(uploadResult.message)
                ? uploadResult.message.join("")
                : uploadResult.message;
              const message = `${result_message}\n`;
              if (!seenErrors.has(message)) {
                errorDetails.push(message);
                seenErrors.add(message);
              }
              // detailのないエラーはCSV全体に対する失敗とみなし、これ以上の分割送信を中断する
              break;
            }
          }
        }
      } catch (err) {
        caught = true;
        const errorMessage = "アップロードに失敗しました。";
        params.handleError(err, errorMessage);
      } finally {
        // 例外が発生した場合は handleError 側で処理済みのため、完了コールバックを呼ばない
        if (!caught) {
          completeMessage = this.createCsvUploadCompleteMessage(
            failed,
            errorDetails
          );
          params.handleUploadComplete(failed, completeMessage);
        }
      }
    };

    reader.onerror = () => {
      params.handleError(
        new Error("ファイルの読取りに失敗しました。"),
        "ファイルの読取りに失敗しました。"
      );
    };

    reader.readAsText(params.file, "shift-jis");
  }

  /*
   * CSVデータを指定行数の単位で分割した配列を返す
   */
  private static splitCsvData(
    csvData: string,
    unit = 200,
    separator: RegExp | string = /\r\n|\n|\r/
  ): string[] {
    const csvLines: string[] = csvData.trim().split(separator);
    const header: string | undefined = csvLines.shift();
    const splitCsvData: string[] = [];
    let unitCsvData: string[] = [];
    const isUnit = (length: number, unit: number): boolean => length === unit;
    const isLastArray = (index: number, length: number): boolean =>
      index === length - 1;
    const createUnitCsvString = (
      unitCsvData: string[],
      separator = "\n"
    ): string => {
      unitCsvData.unshift(header ?? "");
      return unitCsvData.join(separator);
    };

    for (const [index, line] of csvLines.entries()) {
      unitCsvData.push(line);

      if (
        isUnit(unitCsvData.length, unit) ||
        isLastArray(index, csvLines.length)
      ) {
        const unitCsvString: string = createUnitCsvString(unitCsvData);
        splitCsvData.push(unitCsvString);
        unitCsvData = [];
      }
    }

    return splitCsvData;
  }

  /**
   * Base64エンコードした文字列を返す
   * ブラウザ環境で動作するようにTextEncoderとbtoaを使用
   * @param str 変換対象の文字列
   * @private
   */
  private static toBase64String(str: string): string {
    // TextEncoderでUTF-8バイト列に変換
    const encoder = new TextEncoder();
    const uint8Array = encoder.encode(str);
    // Uint8Arrayをバイナリ文字列に変換
    let binaryString = "";
    for (let i = 0; i < uint8Array.length; i++) {
      binaryString += String.fromCharCode(uint8Array[i]);
    }
    // btoaでBase64エンコード
    return btoa(binaryString);
  }

  /**
   * CSVアップロード完了メッセージを返す
   * @param failed
   * @param errorDetails
   */
  private static createCsvUploadCompleteMessage(
    failed: boolean,
    errorDetails: string[]
  ): string {
    let message = "アップロードが完了しました。";
    if (failed) {
      message = "アップロードに失敗しました。\n" + errorDetails.join("");
    }

    return message;
  }
}
